import React, { FunctionComponent, useContext } from "react";
import { useForm } from "react-hook-form";
import {
    Form,
    FormActions,
    FormCheckbox,
    FormDropdown,
    FormFields,
    FormTextField,
    mapToDropdownOption
} from "sprint-common-web";

import { OriginatorsDataContext } from "../../../common/data/lookup-data-providers/OriginatorsDataProvider";
import { DeliveryBucketAccessType, deliveryBucketAccessTypeDropdownOptions } from "./common/DeliveryBucketAccessType";
import { useCreateDeliveryBucketForm } from "./useCreateDeliveryBucketForm";

export interface CreateDeliveryBucketFormProps {
    onCancel(): void;
    onSubmit(): void;
}

export interface CreateDeliveryBucketFormInputs {
    accessType: string;
    uniqueId: string;
    originatorId: string;
    externalRoleArn: string;
    externalId: string;
    useDedicatedStorage: boolean;
}

export const CreateDeliveryBucketForm: FunctionComponent<CreateDeliveryBucketFormProps> = (
    props: CreateDeliveryBucketFormProps
) => {
    const { originators } = useContext(OriginatorsDataContext);

    const { errors, control, formState, reset, handleSubmit, watch } = useForm<CreateDeliveryBucketFormInputs>({
        defaultValues: {
            accessType: "",
            uniqueId: "",
            originatorId: "",
            externalRoleArn: "",
            externalId: "",
            useDedicatedStorage: false
        },
        criteriaMode: "all"
    });

    const { validateUniqueId, addDeliveryBucket } = useCreateDeliveryBucketForm();

    const handleCreateBucket = async ({
        accessType,
        uniqueId,
        originatorId,
        externalId,
        externalRoleArn,
        useDedicatedStorage
    }: CreateDeliveryBucketFormInputs) => {
        await addDeliveryBucket({
            accessType,
            uniqueId,
            originatorId,
            externalRoleArn,
            externalId,
            useDedicatedStorage
        });

        props.onSubmit();

        reset();
    };

    const handleCancel = () => {
        reset();

        props.onCancel();
    };

    return (
        <Form onSubmit={handleSubmit(handleCreateBucket)}>
            <FormFields>
                <FormDropdown
                    options={deliveryBucketAccessTypeDropdownOptions}
                    label="Access Type"
                    name="accessType"
                    control={control}
                    errors={errors}
                    rules={{ required: "Select access type" }}
                />
                {watch("accessType") && (
                    <>
                        <FormTextField
                            name="uniqueId"
                            label="Unique id"
                            control={control}
                            errors={errors}
                            rules={{
                                required: "Enter unique id",
                                validate: validateUniqueId,
                                pattern: {
                                    value: /^(?!\d+\.\d+\.\d+\.\d+$)[a-z0-9]([a-z0-9.-]{1,61}[a-z0-9])?$/,
                                    message:
                                        "Unique id must contain only lowercase letters, digits, hyphens, and dots; must not start or end with a dot or hyphen"
                                }
                            }}
                        />

                        <FormDropdown
                            name="originatorId"
                            label="Originator"
                            options={originators.map((originator) => mapToDropdownOption(originator))}
                            control={control}
                            errors={errors}
                            rules={{
                                required: "Select originator"
                            }}
                        />

                        <FormCheckbox label="Use dedicated storage" name="useDedicatedStorage" control={control} />
                    </>
                )}
                {Number(watch("accessType")) === DeliveryBucketAccessType.Role && (
                    <>
                        <FormTextField
                            name="externalRoleArn"
                            label="Role arn"
                            control={control}
                            errors={errors}
                            rules={{
                                required: "Enter external role arn",
                                pattern: {
                                    value: /^arn:aws:iam::\d{12}:role\/.+/,
                                    message: "Invalid external role arn"
                                }
                            }}
                        />
                        <FormTextField
                            name="externalId"
                            label="External id"
                            control={control}
                            errors={errors}
                            rules={{
                                required: "Enter external id"
                            }}
                        />
                    </>
                )}
            </FormFields>
            <FormActions isLoading={formState.isSubmitting || formState.isValidating} onCancel={handleCancel} />
        </Form>
    );
};
